import { useState, useEffect } from "react";
import { onValue, ref } from "firebase/database";
import moment from "moment";

import { CircularProgress, Card, CardContent, Typography } from "@mui/material";
import { database } from "../firebase";
import SearchForm from "../components/search-form/SearchForm";
import HistoryTable from "../components/results-table/HistoryTable";
import { ROTATION } from "../constants";
import { downloadCSV } from "../utils";
import seasons from "../seasons";
import { useUser } from "../contexts/UserContext";

const ReviewPage = () => {
  const user = useUser();

  const currentSeason = Date.now();

  const [pageLoad, setPageLoad] = useState(true);
  const [gameLog, setGameLog] = useState([]);
  const [searchTerms, setSearchTerms] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [format, setFormat] = useState(ROTATION);
  const [season, setSeason] = useState(
    seasons.filter((s) => s.start < currentSeason && s.end > currentSeason)[0]
  );

  const fetchGames = async () => {
    const dbRef = ref(database, `id/${user.uid}/games`);
    onValue(dbRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const allGames = Object.values(data);
        setGameLog(allGames);
      } else {
        setGameLog([]);
      }
    });
  };

  const seasonSortGames = (games) => {
    if (!season) return games;
    const gamesInSeason = games.filter(
      (game) => game.id > season.start && game.id < season.end
    );
    return gamesInSeason;
  };

  const filterGames = (games) => games.filter((game) => game.format === format);

  const parseGames = () => {
    const searchKeys = Object.keys(searchTerms);
    const foundGames = gameLog.filter((val) => {
      let check = true;
      searchKeys.every((sKey) => {
        if (!searchTerms[sKey]) return true;
        if (searchTerms[sKey] !== val[sKey]) check = false;
        return true;
      });
      return check;
    });

    const seasonGames = seasonSortGames(foundGames);
    const formatGames = filterGames(seasonGames);

    setSearchResults(formatGames);
  };

  const searchGames = () => {
    setPageLoad(true);
    if (Object.values(searchTerms).every((val) => !val)) {
      const seasonGames = seasonSortGames(gameLog);
      const formatResults = filterGames(seasonGames);
      setSearchResults(formatResults);
    } else {
      parseGames(searchTerms);
    }
  };

  useEffect(() => {
    if (user) fetchGames();
  }, [user]);

  const handleSeasonChange = (seasonId) => {
    const chosenSeason = seasons.filter((s) => s.id === seasonId);
    setSeason(chosenSeason[0]);
  };
  const handleFormatChange = (formatValue) => setFormat(formatValue);
  const updateSearchTerms = (query) => setSearchTerms(query);

  useEffect(() => {
    searchGames();
  }, [gameLog, format, season, searchTerms]);

  useEffect(() => {
    if (gameLog.length === 0) {
      setPageLoad(false);
      return;
    }

    const winCount = searchResults.filter(
      (game) => game.result === "WIN"
    ).length;
    const lossCount = searchResults.filter(
      (game) => game.result === "LOSE"
    ).length;

    setWins(winCount);
    setLosses(lossCount);
    setPageLoad(false);
  }, [searchResults]);

  const exportToCSV = () => {
    const joinText = (data, isTitle) =>
      data.reduce(
        (acc, curr, index) =>
          `${acc} ${
            !isTitle && index === 0
              ? moment(curr).format("HH:mm YYYY/MM/DD")
              : curr
          },`,
        ""
      );

    const dataArray = Object.values(searchResults);

    const titles = [
      "Date",
      "Format",
      "Deck",
      "Opponent's Deck",
      "Order",
      "Result",
      "Notes"
    ];
    const titlesText = joinText(titles, true).slice(0, -1);
    const gameArray = dataArray.map((game) =>
      joinText(
        [
          game.id,
          game.format,
          game.deck,
          game.opponent,
          game.order,
          game.result,
          game.notes
        ],
        false
      )
    );
    const gameText = gameArray.join("\n");

    const csvData = `${titlesText}\n${gameText}`;
    downloadCSV(csvData);
  };

  return (
    <>
      <SearchForm
        format={format}
        season={season}
        onSeasonChange={handleSeasonChange}
        onFormSubmit={updateSearchTerms}
        onFormatChange={handleFormatChange}
        onCSVExport={exportToCSV}
      />
      {pageLoad ? (
        <Card>
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            <CircularProgress />
            <Typography variant="subtitle2" sx={{ marginTop: 2 }}>
              Smorc'ing the database for your games, please wait a moment
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <HistoryTable results={searchResults} wins={wins} losses={losses} />
      )}
    </>
  );
};

export default ReviewPage;
